<style scoped>
.map-style-switcher {
	position: absolute;
	top: 10px;
	right: 10px;
	z-index: 1;
	display: flex;
	border-radius: 4px;
	overflow: hidden;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
}

.style-btn {
	border: none;
	padding: 6px 12px;
	font-size: 13px;
	cursor: pointer;
	background-color: #fff;
	color: #333;
}

.style-btn:hover {
	background-color: #e9ecef;
}

.style-btn.active {
	background-color: #0d6efd;
	color: #fff;
}

.pool-table-section {
	padding: 16px;
	height: 100%;
	overflow-y: auto;
}

.pool-table {
	width: 100%;
	margin-bottom: 0;
	border-collapse: collapse;
}

.pool-table th,
.pool-table td {
	padding: 0.75rem;
	border-bottom: 1px solid #dee2e6;
	text-align: left;
}

.pool-table thead {
	position: sticky;
	top: 0;
	background-color: #f8f9fa;
	z-index: 1;
}

.pool-table tbody tr:hover {
	background-color: rgba(13, 110, 253, 0.05);
}

.pool-table tbody tr.row-selected {
	background-color: rgba(13, 110, 253, 0.15);
}

.pool-table tbody tr.clickable-row {
	cursor: pointer;
}

.badge-condition {
	display: inline-block;
	padding: 0.35em 0.65em;
	font-size: 0.75em;
	font-weight: 700;
	line-height: 1;
	color: #fff;
	text-align: center;
	border-radius: 0.25rem;
	text-transform: capitalize;
}
</style>

<template>
	<HorizontalSplit>
		<template #top>
			<div style="position: relative; width: 100%; height: 100%">
				<mgl-map
					:map-style="styleURL(activeStyle)"
					:bounds="mapBounds"
					:fit-bounds-options="{ padding: 50, duration: 1000 }"
					@map:idle="queryPoolFeatures"
					@map:moveend="queryPoolFeatures"
				>
					<mgl-geo-json-source
						v-if="serviceAreaGeoJSON"
						source-id="service-area-geojson"
						:data="serviceAreaGeoJSON"
					>
						<mgl-fill-layer
							layer-id="service-area-fill"
							:paint="{ 'fill-color': '#888', 'fill-opacity': 0.1 }"
						/>
					</mgl-geo-json-source>
					<mgl-vector-source source-id="tegola" :tiles="tegolaTiles">
						<mgl-line-layer
							layer-id="parcel"
							source-layer="parcel"
							:minzoom="14"
							:paint="{ 'line-color': '#0f0' }"
						/>
						<mgl-line-layer
							layer-id="service-area"
							source-layer="service-area-bounds"
							:paint="{ 'line-color': '#f00' }"
						/>
						<mgl-circle-layer
							layer-id="feature-pool-address"
							source-layer="feature-pool-address"
							:paint="paintConfigPool"
							@mouseenter="onPoolMouseEnter"
							@mouseleave="onPoolMouseLeave"
							@click="onPoolClick"
						/>
					</mgl-vector-source>
				</mgl-map>
				<div class="map-style-switcher">
					<button
						class="style-btn"
						:class="{ active: activeStyle === ALIDADE_SMOOTH }"
						@click="activeStyle = ALIDADE_SMOOTH"
					>
						Map
					</button>
					<button
						class="style-btn"
						:class="{ active: activeStyle === ALIDADE_SATELLITE }"
						@click="activeStyle = ALIDADE_SATELLITE"
					>
						Satellite
					</button>
				</div>
			</div>
		</template>
		<template #bottom>
			<div class="pool-table-section">
				<table class="pool-table">
					<thead>
						<tr>
							<th>Site ID</th>
							<th colspan="3">Address</th>
							<th>Condition</th>
							<th>Condition Date</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						<tr
							v-for="(pool, index) in pools"
							:key="index"
							class="clickable-row"
							:class="{ 'row-selected': selectedPoolIndex === index }"
							@click="selectPool(index)"
						>
							<td>{{ pool.siteId }}</td>
							<td>{{ pool.addressNumber }}</td>
							<td>{{ pool.addressStreet }}</td>
							<td>{{ pool.addressLocality }}</td>
							<td>
								<span
									class="badge-condition"
									:style="{ backgroundColor: conditionColor(pool.condition) }"
								>
									{{ pool.condition }}
								</span>
							</td>
							<td>{{ pool.conditionDate }}</td>
							<td class="text-end">
								<Tooltip title="See details and history" placement="top">
									<RouterLink
										:to="`/_/review/feature/${pool.featureId}`"
										class="btn btn-sm btn-outline-secondary"
										@click.stop
									>
										<i class="bi bi-info-circle"></i>
									</RouterLink>
								</Tooltip>
							</td>
						</tr>
						<tr v-if="pools.length === 0">
							<td colspan="7" class="text-center text-muted py-4">
								No pools visible in the current map area
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</template>
	</HorizontalSplit>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { RouterLink } from "vue-router";
import maplibregl from "maplibre-gl";
import {
	MglMap,
	MglVectorSource,
	MglLineLayer,
	MglCircleLayer,
	MglGeoJsonSource,
	MglFillLayer,
	useMap,
} from "vue-maplibre-gl";
import HorizontalSplit from "@/components/layout/HorizontalSplit.vue";
import Tooltip from "@/components/Tooltip.vue";
import { ALIDADE_SMOOTH, ALIDADE_SATELLITE } from "@/map/constants";
import { mapStyleURL } from "@/map/util";
import { useSessionStore } from "@/store/session";

const session = useSessionStore();
const mapInstance = useMap();

const activeStyle = ref(ALIDADE_SMOOTH);
const flyToBounds = ref<maplibregl.LngLatBounds>();
const selectedPoolIndex = ref<number | null>(null);

const tegolaTiles = computed<string[]>(() => {
	const base = session.urls?.tegola ?? "";
	return [`${base}maps/nidus/{z}/{x}/{y}`];
});

function styleURL(name: string): string {
	return mapStyleURL(name);
}

const mapBounds = computed<maplibregl.LngLatBounds | undefined>(() => {
	return flyToBounds.value ?? serviceAreaBounds();
});

function serviceAreaBounds(): maplibregl.LngLatBounds | undefined {
	const area = session.organization?.service_area;
	if (!area) return undefined;
	return new maplibregl.LngLatBounds(
		[area.min.longitude, area.min.latitude],
		[area.max.longitude, area.max.latitude],
	);
}

const serviceAreaGeoJSON = computed(() => {
	const area = session.organization?.service_area;
	if (!area) return undefined;
	const { min, max } = area;
	return {
		type: "FeatureCollection" as const,
		features: [
			{
				type: "Feature" as const,
				properties: {},
				geometry: {
					type: "Polygon" as const,
					coordinates: [
						[
							[min.longitude, min.latitude],
							[max.longitude, min.latitude],
							[max.longitude, max.latitude],
							[min.longitude, max.latitude],
							[min.longitude, min.latitude],
						],
					],
				},
			},
		],
	};
});

const renderedPoolFeatures = ref<any[]>([]);

function queryPoolFeatures(): void {
	const m = mapInstance.map;
	if (!m) return;
	if (!m.getLayer("feature-pool-address")) return;
	const features = m.queryRenderedFeatures(undefined, {
		layers: ["feature-pool-address"],
	});
	renderedPoolFeatures.value = features.filter(
		(f: any) => f.source === "tegola",
	);
}

function onPoolMouseEnter(): void {
	const canvas = mapInstance.map?.getCanvas();
	if (canvas) canvas.style.cursor = "pointer";
}

function onPoolMouseLeave(): void {
	const canvas = mapInstance.map?.getCanvas();
	if (canvas) canvas.style.cursor = "";
}

interface PoolRow {
	siteId: number | string;
	addressNumber: string;
	addressStreet: string;
	addressLocality: string;
	condition: string;
	conditionDate: string;
	featureId: number | string;
}

const pools = computed<PoolRow[]>(() => {
	return renderedPoolFeatures.value.map((f: any): PoolRow => {
		const p = f.properties;
		return {
			siteId: p.site_id,
			addressNumber: p.address_number ?? "",
			addressStreet: p.address_street ?? "",
			addressLocality: p.address_locality ?? "",
			condition: p.condition,
			conditionDate: formatDate(p.condition_date),
			featureId: f.id,
		};
	});
});

const CONDITION_COLORS: { condition: string; color: string }[] = [
	{ condition: "blue", color: "#0d6efd" },
	{ condition: "covered", color: "#ba3b46" },
	{ condition: "green", color: "#198754" },
	{ condition: "brown", color: "#8B4513" },
	{ condition: "murky", color: "#6c757d" },
	{ condition: "dry", color: "#fd7e14" },
	{ condition: "false pool", color: "#dc3545" },
];

function buildConditionMatchExpression(prop: string, fallback: string): any[] {
	const expr: any[] = ["match", ["get", prop]];
	for (const entry of CONDITION_COLORS) {
		expr.push(entry.condition, entry.color);
	}
	expr.push(fallback);
	return expr;
}

const conditionColorMap: Record<string, string> = Object.fromEntries(
	CONDITION_COLORS.map((c) => [c.condition, c.color]),
);

function conditionColor(condition: string): string {
	return conditionColorMap[condition.toLowerCase()] ?? "#6c757d";
}

function formatDate(value: unknown): string {
	if (!value) return "";
	const d = new Date(value as string);
	if (isNaN(d.getTime())) return String(value);
	return d.toLocaleDateString();
}

function selectPool(index: number): void {
	selectedPoolIndex.value = index;
	const feature = renderedPoolFeatures.value[index];
	if (!feature?.geometry?.coordinates) return;
	const [lng, lat] = feature.geometry.coordinates;
	const offset = 0.001;
	flyToBounds.value = new maplibregl.LngLatBounds(
		[lng - offset, lat - offset],
		[lng + offset, lat + offset],
	);
}

function onPoolClick(e: any): void {
	const clicked = e.features?.[0];
	if (!clicked?.properties?.site_id) return;
	const clickedSiteId = clicked.properties.site_id;
	const index = renderedPoolFeatures.value.findIndex(
		(f: any) => f.properties?.site_id === clickedSiteId,
	);
	if (index >= 0) {
		selectPool(index);
	}
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const paintConfigPool: any = {
	"circle-color": buildConditionMatchExpression("condition", "#6c757d"),
	"circle-radius": 7,
	"circle-stroke-color": "#333",
	"circle-stroke-width": 2,
};
</script>
